<script>
import { mapActions, mapState } from 'pinia';

import { useUserStore } from '../../../store/useUserStore.js';
import { useCampaignStore } from '../../../store/useCampaignStore.js';
import { useGroupStore } from '../../../store/useGroupStore.js';

export default {
  name: 'UserNavDropdown',
  data() {
    return {
      path: window.location.pathname,
      isCampaignsDropdownOpen: false,
    };
  },
  computed: {
    ...mapState(useUserStore, ['userLoginData']),
    ...mapState(useCampaignStore, ['userCampaignList']),
    ...mapState(useGroupStore, ['userHasCreatedGroups']),
  },
  mounted() {
    this.getUserCampaigns();
    this.getUserGroups();
  },
  methods: {
    handleLogout() {
      localStorage.removeItem('userLoginData');
      sessionStorage.removeItem('userPlanStorage');
      localStorage.setItem('isRegularUser', true);
      localStorage.setItem('saveTimestampAfter24HoursLoguedOut', Date.now().toString());
      localStorage.setItem('justLoggedOut', true);
    },
    toggleCampaignsDropdown() {
      this.isCampaignsDropdownOpen = !this.isCampaignsDropdownOpen;
    },
    ...mapActions(useCampaignStore, ['getUserCampaigns']),
    ...mapActions(useGroupStore, ['getUserGroups']),
  },
};
</script>

<template>
  <div class="dropdown">
    <a class="site-header__main-nav-item site-header__main-nav-item--icon-only site-header__main-nav-avatar" data-toggle-X="dropdown">
      <img :src="userLoginData.img" class="js-sitewide-replace-user-img site-header__user-image" width="36" height="36" alt="Profile image" />
    </a>

    <ul class="dropdown-menu site-header__nav-menu pull-right">
      <li>
        <a class="site-header__nav-user-profile" href="/user/profile">
          <img :src="userLoginData.img" class="js-sitewide-replace-user-img site-header__nav-user-profile-image" width="36" height="36" alt="Profile image" />

          <div>
            <div class="js-sitewide-replace-user-name site-header__nav-user-profile-name">My Account</div>
            <div class="site-header__nav-user-profile-meta">View profile</div>
          </div>
        </a>
      </li>
      <li>
        <a href="/user/my-activity" :class="{ 'site-header__nav-menu-item': true, 'site-header__nav-menu-item--active': path == '/user/my-activity' }">
          <div class="site-header__nav-menu-item-heading">My activity</div>
        </a>
      </li>
      <li class="site-header__nav-menu-item--has-dropdown">
        <a
          @click.prevent="toggleCampaignsDropdown"
          :class="{
            'site-header__nav-menu-item': true,
            'site-header__nav-menu-item--active': path == '/user/campaigns',
          }"
        >
          <div class="site-header__nav-menu-item-heading">
            My Campaigns
            <i
              class="fas fa-chevron-down site-header__nav-menu-item-chevron"
              :class="{ 'site-header__nav-menu-item-chevron--open': isCampaignsDropdownOpen }"
            ></i>
          </div>
        </a>
        <ul v-show="isCampaignsDropdownOpen" class="site-header__nav-menu-dropdown">
          <template v-if="userCampaignList.length > 0">
            <li v-for="campaign in userCampaignList" :key="campaign.id">
              <a :href="campaign.permalink" class="site-header__nav-menu-item site-header__nav-menu-item--sub" @click="toggleCampaignsDropdown">
                <div class="site-header__nav-menu-item-heading">{{ campaign.title }}</div>
              </a>
            </li>
          </template>
          <li v-else>
            <a href="/user/campaigns/create" class="site-header__nav-menu-item site-header__nav-menu-item--sub" @click="toggleCampaignsDropdown">
              <div class="site-header__nav-menu-item-heading">Start a Campaign</div>
            </a>
          </li>
        </ul>
      </li>
      <li v-if="userHasCreatedGroups">
        <a
          href="/user/groups"
          :class="{
            'site-header__nav-menu-item': true,
            'site-header__nav-menu-item--active': path == '/user/groups',
          }"
        >
          <div class="site-header__nav-menu-item-heading">My Groups</div>
        </a>
      </li>
      <li>
        <a
          href="/user/settings"
          :class="{
            'site-header__nav-menu-item': true,
            'site-header__nav-menu-item--active': path.startsWith('/user/settings') || path.startsWith('/user/emails'),
          }"
        >
          <div class="site-header__nav-menu-item-heading">Settings</div>
        </a>
      </li>
      <li>
        <form method="POST" action="/user/logout">
          <button @click="handleLogout" type="submit" class="inline-button site-header__nav-menu-item">
            <div class="site-header__nav-menu-item-heading">Sign out</div>
          </button>
        </form>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.dropdown-menu.site-header__nav-menu {
  width: 100% !important;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}
.site-header__nav-menu-item--has-dropdown {
  position: relative;
  cursor: pointer;
}
.site-header__nav-menu-item-chevron {
  margin-left: 8px;
  font-size: 10px;
  transition: transform 0.2s ease;
}
.site-header__nav-menu-item-chevron--open {
  transform: rotate(180deg);
}
.site-header__nav-menu-dropdown {
  list-style: none;
  padding: 0;
  margin: 0;
  background-color: var(--white);
  border-top: 1px solid var(--neutral-1);
  width: 100%;
  box-sizing: border-box;
}
.site-header__nav-menu-dropdown > li {
  margin: 0;
  padding: 0;
  width: 100%;
  box-sizing: border-box;
}
.site-header__nav-menu-item--sub {
  display: block !important;
  width: 100% !important;
  padding-left: 22px !important;
  margin: 0 !important;
  border-bottom: none !important;
  box-sizing: border-box !important;
}
.site-header__nav-menu-item--sub .site-header__nav-menu-item-heading {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.site-header__nav-menu-item--sub:hover {
  background-color: var(--neutral-1) !important;
}
.site-header__nav-menu-dropdown > li:last-child > .site-header__nav-menu-item--sub {
  border-bottom: 1px solid var(--neutral-1) !important;
}
</style>
